<template>
  <transition name="pay-range-filter-modal" appear>
    <div
      v-if="value"
      class="pay-range-filter-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pay-range-filter-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="pay-range-filter-modal__card">
        <div class="pay-range-filter-modal__header">
          <span id="pay-range-filter-modal-title" class="pay-range-filter-modal__title">
            Filter pay ranges
          </span>
          <button
            type="button"
            class="pay-range-filter-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="pay-range-filter-modal__filters-row">
          <div class="pay-range-filter-modal__column">
            <span class="pay-range-filter-modal__column-title">Status</span>
            <div class="pay-range-filter-modal__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="statuses[option.value]"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>

          <div class="pay-range-filter-modal__column pay-range-filter-modal__column--radio">
            <span class="pay-range-filter-modal__column-title">Archived</span>
            <RadioInput
              label="Archived"
              :availableItems="archivedOptions"
              :checkedItem="archivedCheckedLabel"
              :uncheckedValue="archivedClearKey"
              @changeradio="onArchivedChange"
            />
          </div>

          <div class="pay-range-filter-modal__column pay-range-filter-modal__column--radio">
            <span class="pay-range-filter-modal__column-title">Pay basis</span>
            <RadioInput
              label="Pay basis"
              :availableItems="payBasisOptions"
              :checkedItem="payBasisCheckedLabel"
              :uncheckedValue="payBasisClearKey"
              @changeradio="onPayBasisChange"
            />
          </div>

          <div class="pay-range-filter-modal__column pay-range-filter-modal__column--radio">
            <span class="pay-range-filter-modal__column-title">Work mode</span>
            <RadioInput
              label="Work mode"
              :availableItems="workModeOptions"
              :checkedItem="workModeCheckedLabel"
              :uncheckedValue="workModeClearKey"
              @changeradio="onWorkModeChange"
            />
          </div>
        </div>

        <div class="pay-range-filter-modal__divider" />

        <div class="pay-range-filter-modal__picker">
          <CurrencyPicker
            :key="`filter-currency-${pickerSessionKey}`"
            pickerLabel="Currency"
            label="Currency"
            searchLabel="Select Currency"
            listSource="operating"
            :items="[]"
            :value="currency"
            :isSelectedItemRemove="true"
            :pickerPopUp="true"
            @input="setCurrency"
            @clear-selection="clearCurrency"
          />
        </div>

        <div class="pay-range-filter-modal__divider" />

        <div class="pay-range-filter-modal__picker">
          <CountryPicker
            :key="`filter-country-${pickerSessionKey}`"
            pickerLabel="Country"
            label="Type to search"
            searchLabel="Search country"
            listSource="operating"
            :useStaticFallback="false"
            :items="[]"
            :value="country"
            :isSelectedItemRemove="true"
            :pickerPopUp="true"
            @input="setCountry"
            @clear-selection="clearCountry"
          />
        </div>

        <div class="pay-range-filter-modal__divider" />

        <div class="pay-range-filter-modal__footer">
          <button type="button" class="pay-range-filter-modal__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import CurrencyPicker from "@components/CurrencyPicker/CurrencyPicker.vue";
import CountryPicker from "@components/CountryPicker/CountryPicker.vue";

const DEFAULT_STATUSES = { draft: true, active: true, retired: false };
const DEFAULT_INCLUDE_ARCHIVED = false;
const DEFAULT_PAY_BASIS = null;
const DEFAULT_WORK_MODE = null;

const PAY_BASIS_LABEL_BY_VALUE = {
  HOURLY: "Hourly",
  MONTHLY: "Monthly",
  ANNUAL: "Annual",
};

const PAY_BASIS_VALUE_BY_LABEL = {
  "No Filter": null,
  Hourly: "HOURLY",
  Monthly: "MONTHLY",
  Annual: "ANNUAL",
};

const WORK_MODE_LABEL_BY_VALUE = {
  ON_SITE: "On Site",
  HYBRID: "Hybrid",
  REMOTE: "Remote",
};

const WORK_MODE_VALUE_BY_LABEL = {
  "No Filter": null,
  "On Site": "ON_SITE",
  Hybrid: "HYBRID",
  Remote: "REMOTE",
};

const props = defineProps({
  value: { type: Boolean, default: false },
  initialStatuses: {
    type: Object,
    default: () => ({ draft: true, active: true, retired: false }),
  },
  initialIncludeArchived: { type: Boolean, default: false },
  initialPayBasis: {
    type: String,
    default: null,
    validator: (v) => v === null || ["HOURLY", "MONTHLY", "ANNUAL"].includes(v),
  },
  initialWorkMode: {
    type: String,
    default: null,
    validator: (v) =>
      v === null || ["ON_SITE", "HYBRID", "REMOTE"].includes(v),
  },
  initialCurrency: { type: Object, default: null },
  initialCountry: { type: Object, default: null },
});

const emit = defineEmits(["input", "close", "apply", "reset"]);

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "In Force", value: "active" },
  { label: "Retired", value: "retired" },
];

const ARCHIVED_SHOW_LABEL = "Show";
const ARCHIVED_HIDE_LABEL = "Do Not Show";
const archivedOptions = [{ label: ARCHIVED_HIDE_LABEL }, { label: ARCHIVED_SHOW_LABEL }];
const payBasisOptions = [
  { label: "No Filter" },
  { label: "Hourly" },
  { label: "Monthly" },
  { label: "Annual" },
];
const workModeOptions = [
  { label: "No Filter" },
  { label: "On Site" },
  { label: "Hybrid" },
  { label: "Remote" },
];

const archivedClearKey = ref("");
const payBasisClearKey = ref("");
const workModeClearKey = ref("");
/** Remount pickers on open so operating lists reload with scroll pagination. */
const pickerSessionKey = ref(0);

const statuses = ref({ ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) });
const includeArchived = ref(props.initialIncludeArchived);
const payBasis = ref(props.initialPayBasis);
const workMode = ref(props.initialWorkMode);
const currency = ref(props.initialCurrency);
const country = ref(props.initialCountry);

const archivedCheckedLabel = computed(() =>
  includeArchived.value ? ARCHIVED_SHOW_LABEL : ARCHIVED_HIDE_LABEL
);
const payBasisCheckedLabel = computed(
  () => PAY_BASIS_LABEL_BY_VALUE[payBasis.value] || "No Filter"
);
const workModeCheckedLabel = computed(
  () => WORK_MODE_LABEL_BY_VALUE[workMode.value] || "No Filter"
);

watch(
  () => props.value,
  (open) => {
    if (open) {
      syncFromProps();
      pickerSessionKey.value += 1;
    }
  },
  { immediate: true }
);

function syncFromProps() {
  statuses.value = { ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) };
  includeArchived.value = props.initialIncludeArchived;
  payBasis.value = props.initialPayBasis;
  workMode.value = props.initialWorkMode;
  currency.value = props.initialCurrency;
  country.value = props.initialCountry;
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function onArchivedChange(payload) {
  includeArchived.value = payload?.value === ARCHIVED_SHOW_LABEL;
}

function onPayBasisChange(payload) {
  payBasis.value = PAY_BASIS_VALUE_BY_LABEL[payload?.value] ?? null;
}

function onWorkModeChange(payload) {
  workMode.value = WORK_MODE_VALUE_BY_LABEL[payload?.value] ?? null;
}

function setCurrency(item) {
  currency.value = item;
}

function setCountry(item) {
  country.value = item;
}

function clearCurrency() {
  currency.value = null;
}

function clearCountry() {
  country.value = null;
}

function handleReset() {
  statuses.value = { ...DEFAULT_STATUSES };
  includeArchived.value = DEFAULT_INCLUDE_ARCHIVED;
  payBasis.value = DEFAULT_PAY_BASIS;
  workMode.value = DEFAULT_WORK_MODE;
  currency.value = null;
  country.value = null;
  archivedClearKey.value = `reset-${Date.now()}`;
  payBasisClearKey.value = `reset-pb-${Date.now()}`;
  workModeClearKey.value = `reset-wm-${Date.now()}`;
  pickerSessionKey.value += 1;
  emit("reset");
}

function handleApply() {
  emit("apply", {
    status: statusOptions
      .filter((option) => statuses.value[option.value])
      .map((option) => option.value),
    includeArchived: includeArchived.value,
    payBasis: payBasis.value,
    workMode: workMode.value,
    currency: currency.value,
    country: country.value,
  });
  emit("input", false);
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.pay-range-filter-modal-enter-active,
.pay-range-filter-modal-leave-active {
  transition: opacity 180ms ease;
}

.pay-range-filter-modal-enter-from,
.pay-range-filter-modal-leave-to {
  opacity: 0;
}

.pay-range-filter-modal-enter-active .pay-range-filter-modal__card,
.pay-range-filter-modal-leave-active .pay-range-filter-modal__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.pay-range-filter-modal-enter-from .pay-range-filter-modal__card,
.pay-range-filter-modal-leave-to .pay-range-filter-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .pay-range-filter-modal-enter-active,
  .pay-range-filter-modal-leave-active,
  .pay-range-filter-modal-enter-active .pay-range-filter-modal__card,
  .pay-range-filter-modal-leave-active .pay-range-filter-modal__card {
    transition: none !important;
  }
}

.pay-range-filter-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.pay-range-filter-modal__card {
  width: 100%;
  max-width: 1032px;
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
  overflow: visible;
}

.pay-range-filter-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.pay-range-filter-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.pay-range-filter-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pay-range-filter-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.pay-range-filter-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

::v-deep .container.select-picker {
  padding: 0px !important;
}

.pay-range-filter-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.pay-range-filter-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pay-range-filter-modal__column-title {
  color: #667085;
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
}

.pay-range-filter-modal__picker .pay-range-filter-modal__column-title {
  color: #344054;
  font-size: 14px;
  line-height: 20px;
}

.pay-range-filter-modal__options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Keep Status checkbox rows at Figma 16px control / 20px row height */
.pay-range-filter-modal__options ::v-deep .filter-checkbox {
  height: 20px;
  min-height: 20px;
}

.pay-range-filter-modal__options ::v-deep .filter-checkbox .v-btn {
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  margin: 0 !important;
}

.pay-range-filter-modal__options ::v-deep .filter-checkbox .v-icon {
  font-size: 16px !important;
  height: 16px !important;
  width: 16px !important;
}

.pay-range-filter-modal__options ::v-deep .filter-checkbox .label {
  margin-left: 8px !important;
}

/*
  Radio columns use the same modal column-title as Status (Figma).
  Hide RadioInput's internal label so asymmetric .filter-items padding
  cannot shift option rows out of alignment.
*/
.pay-range-filter-modal__column--radio ::v-deep .filter-items {
  display: none !important;
}

.pay-range-filter-modal__column--radio ::v-deep > div > .pl-2 {
  padding-left: 0 !important;
}

.pay-range-filter-modal__column--radio ::v-deep .v-input--radio-group {
  margin: 0;
  padding: 0;
}

.pay-range-filter-modal__column--radio ::v-deep .v-input--selection-controls {
  margin: 0;
  padding: 0;
}

.pay-range-filter-modal__column--radio ::v-deep .v-input--selection-controls .v-input__slot {
  margin: 0;
}

.pay-range-filter-modal__column--radio ::v-deep .v-input--radio-group__input {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pay-range-filter-modal__column--radio ::v-deep .v-radio {
  height: 20px !important;
  min-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
  align-items: center;
}

.pay-range-filter-modal__column--radio ::v-deep .v-input--selection-controls__input {
  width: 16px !important;
  height: 16px !important;
  margin: 0 8px 0 0 !important;
  align-self: center;
}

.pay-range-filter-modal__column--radio ::v-deep .v-input--selection-controls__ripple {
  display: none;
}

.pay-range-filter-modal__column--radio ::v-deep .v-icon {
  font-size: 16px !important;
  height: 16px !important;
  width: 16px !important;
  line-height: 16px !important;
}

.pay-range-filter-modal__column--radio ::v-deep .v-input--selection-controls .v-radio > .v-label {
  margin-left: 0 !important;
  line-height: 20px;
  height: 20px;
}

.pay-range-filter-modal__column--radio ::v-deep .filter-radio .mdi-radiobox-marked::before {
  content: url("../assets/checkedRadioIconRing.svg") !important;
}

.pay-range-filter-modal__divider {
  height: 1px;
  width: 100%;
  background: #e5e7eb;
}

.pay-range-filter-modal__picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 !important;
  width: 100%;
}

/* Match create-dialog pickers: input + dropdown span the full modal width. */
.pay-range-filter-modal__picker ::v-deep .select-picker {
  width: 100%;
}

.pay-range-filter-modal__picker ::v-deep .custom-response-picker {
  width: 100% !important;
  max-width: 100%;
}

.pay-range-filter-modal__picker ::v-deep .list-picker-container {
  width: 100% !important;
  max-width: 100%;
  box-sizing: border-box;
}

.pay-range-filter-modal__picker ::v-deep .res-custom-list-picker {
  width: 100% !important;
  max-width: 100%;
  box-sizing: border-box;
}

.pay-range-filter-modal__picker ::v-deep .res-v-list-item {
  min-width: 0 !important;
  width: 100%;
}

.pay-range-filter-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.pay-range-filter-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.pay-range-filter-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}

.pay-range-filter-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}
</style>
